import { useEffect, useState } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Copy, RefreshCw, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { getBuildInfo, type BuildInfo } from "@/services/buildInfoService";
import { checkForOtaUpdate } from "@/services/otaUpdateService";

type Row = { label: string; value: string };
type Section = { title: string; rows: Row[] };

function fmt(value: string | undefined, fallback = "—"): string {
  return value && value.length > 0 ? value : fallback;
}

function fmtDate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function buildSections(info: BuildInfo): Section[] {
  const naNative = info.isNative ? "—" : "n/a (web)";

  return [
    {
      title: "App",
      rows: [
        { label: "App name", value: fmt(info.appName, naNative) },
        { label: "App ID", value: fmt(info.appId, naNative) },
        { label: "Version name", value: fmt(info.versionName, naNative) },
        { label: "Version code", value: fmt(info.versionCode, naNative) },
        { label: "Platform", value: fmt(info.platform) },
      ],
    },
    {
      title: "OTA Package",
      rows: [
        {
          label: "Current bundle",
          value: info.isNative
            ? fmt(info.currentBundleId, "built-in (APK)")
            : naNative,
        },
        { label: "Latest bundle", value: fmt(info.latestBundleId) },
        { label: "Update pending", value: info.otaPending ? "Yes" : "No" },
        { label: "Last deployed", value: fmtDate(info.otaUpdatedAt) },
      ],
    },
    {
      title: "Build / Source",
      rows: [
        { label: "Git commit", value: fmt(info.gitSha) },
        { label: "Git branch", value: fmt(info.gitBranch) },
        { label: "Build time", value: fmtDate(info.buildTime) },
        { label: "Build mode", value: fmt(info.appEnv) },
      ],
    },
    {
      title: "Environment",
      rows: [
        { label: "Environment", value: fmt(info.environment) },
        { label: "Firebase project", value: fmt(info.firebaseProject) },
        { label: "Chatbot URL", value: fmt(info.chatbotUrl) },
        { label: "Log level", value: fmt(info.logLevel) },
      ],
    },
  ];
}

export default function BuildInfoPage() {
  const navigate = useNavigate();
  const [info, setInfo] = useState<BuildInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setInfo(await getBuildInfo());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const sections = info ? buildSections(info) : [];

  const handleCopy = async () => {
    const report = sections
      .map(
        (s) =>
          `${s.title}\n` +
          s.rows.map((r) => `  ${r.label}: ${r.value}`).join("\n"),
      )
      .join("\n\n");
    try {
      await navigator.clipboard.writeText(report);
      toast.success("Build info copied to clipboard");
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const handleCheckUpdates = async () => {
    setChecking(true);
    try {
      toast.info("Checking for updates…");
      await checkForOtaUpdate();
      // If an update was applied the webview reloads; otherwise refresh the view.
      await load();
      toast.success("Update check complete");
    } catch {
      toast.error("Update check failed");
    } finally {
      setChecking(false);
    }
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/tools")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <PageHeader
            title="Build Info"
            subtitle="Version, OTA, and environment details for this build"
          />
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCheckUpdates}
            disabled={checking || loading}
          >
            {checking ? (
              <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1.5" />
            )}
            Check for updates now
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            disabled={loading || !info}
          >
            <Copy className="h-4 w-4 mr-1.5" />
            Copy report
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-muted-foreground">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
            Gathering build info…
          </div>
        ) : (
          <div className="space-y-4">
            {sections.map((section) => (
              <Card key={section.title}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{section.title}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 divide-y divide-border">
                  {section.rows.map((row) => (
                    <div
                      key={row.label}
                      className="flex items-start justify-between gap-4 py-2 text-sm"
                    >
                      <span className="text-muted-foreground shrink-0">
                        {row.label}
                      </span>
                      <span className="font-mono text-right break-all">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
